import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import DOMPurify from 'dompurify';
import type { AIInsights, Cluster } from '@/services/types/api.types';

export function useInsightsDragDrop(insights: Ref<AIInsights | null>, onSave: () => void) {
  const $q = useQuasar();
  const { t } = useI18n();

  const dragOverElement = ref<string | null>(null);
  const editingItems = ref<Set<any>>(new Set());

  // Keep dialog HTML safe while highlighting item names
  const boldHtml = (text: string) => `<strong>${text}</strong>`;
  const dialogHtml = (key: string, params: Record<string, string>) =>
    DOMPurify.sanitize(t(key, params), { ALLOWED_TAGS: ['strong'], ALLOWED_ATTR: [] });

  function isDragDisabled() {
    return editingItems.value.size > 0;
  }

  function startEdit(item: any) {
    editingItems.value.add(item);
  }

  function finishEdit(item: any) {
    editingItems.value.delete(item);
  }

  function onDragStart(event: DragEvent, item: any, type: 'theme' | 'idea', sourceTheme?: string) {
    if (editingItems.value.size > 0) {
      event.preventDefault();
      return;
    }

    if (!event.dataTransfer) return;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        item,
        type,
        sourceTheme,
      }),
    );
  }

  function onDragOver(event: DragEvent) {
    if (editingItems.value.size > 0) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  function onDragEnter(event: DragEvent, targetItem: any, targetType: 'theme' | 'idea') {
    if (editingItems.value.size > 0) {
      return;
    }

    event.preventDefault();
    const elementId = targetType === 'theme' ? `theme-${targetItem.theme}` : `idea-${targetItem.representative_idea}`;
    dragOverElement.value = elementId;
  }

  function onDragLeave(event: DragEvent) {
    if (event.target === event.currentTarget) {
      dragOverElement.value = null;
    }
  }

  function onDrop(event: DragEvent, targetItem: any, targetType: 'theme' | 'idea') {
    if (editingItems.value.size > 0) {
      return;
    }

    event.preventDefault();
    dragOverElement.value = null;

    if (!event.dataTransfer) return;

    try {
      const dragData = JSON.parse(event.dataTransfer.getData('application/json'));
      const { item: sourceItem, type: sourceType, sourceTheme } = dragData;

      if (sourceType === 'theme' && targetType === 'theme') {
        mergeThemes(sourceItem, targetItem);
      } else if (sourceType === 'idea' && targetType === 'idea') {
        mergeIdeas(sourceItem, targetItem);
      } else if (sourceType === 'idea' && targetType === 'theme') {
        moveIdeaToTheme(sourceItem, targetItem, sourceTheme);
      } else if (sourceType === 'theme' && targetType === 'idea') {
        // Prevent dropping a theme onto an idea; warn user (i18n)
        $q.notify({
          type: 'warning',
          message: t('consultation.ai.invalidDrop.themeOnIdea'),
          position: 'top',
        });
      }
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  }

  function mergeThemes(sourceTheme: Cluster, targetTheme: Cluster) {
    if (sourceTheme.theme === targetTheme.theme) {
      $q.notify({
        type: 'warning',
        message: t('consultation.ai.mergeThemes.sameTheme'),
        position: 'top',
      });
      return;
    }

    $q.dialog({
      title: t('consultation.ai.mergeThemes.title'),
      message: dialogHtml('consultation.ai.mergeThemes.message', {
        source: boldHtml(sourceTheme.theme),
        target: boldHtml(targetTheme.theme),
      }),
      html: true,
      ok: {
        label: t('global.yes'),
        color: 'primary',
      },
      cancel: {
        label: t('global.no'),
        color: 'negative',
      },
    }).onOk(() => {
      targetTheme.ideas.push(...sourceTheme.ideas);

      const clusters = insights.value?.clusters;
      if (clusters) {
        const sourceIndex = clusters.findIndex((c) => c.theme === sourceTheme.theme);
        if (sourceIndex !== -1) {
          clusters.splice(sourceIndex, 1);
          $q.notify({
            type: 'positive',
            message: t('consultation.ai.mergeThemes.success', { target: targetTheme.theme }),
            position: 'top',
          });
        }
      }

      onSave();
    });
  }

  function mergeIdeas(sourceIdea: any, targetIdea: any) {
    if (sourceIdea.representative_idea === targetIdea.representative_idea) {
      $q.notify({
        type: 'warning',
        message: t('consultation.ai.mergeIdeas.sameIdea'),
        position: 'top',
      });
      return;
    }

    $q.dialog({
      title: t('consultation.ai.mergeIdeas.title'),
      message: dialogHtml('consultation.ai.mergeIdeas.message', {
        source: boldHtml(sourceIdea.representative_idea),
        target: boldHtml(targetIdea.representative_idea),
      }),
      html: true,
      ok: {
        label: t('global.yes'),
        color: 'primary',
      },
      cancel: {
        label: t('global.no'),
        color: 'negative',
      },
    }).onOk(() => {
      const originalCount = targetIdea.answer_ids.length;

      targetIdea.answer_ids.push(...sourceIdea.answer_ids);
      targetIdea.answer_ids = [...new Set(targetIdea.answer_ids)];

      const clusters = insights.value?.clusters;
      if (clusters) {
        let removed = false;
        for (const cluster of clusters) {
          const ideaIndex = cluster.ideas.findIndex(
            (idea) => idea.representative_idea === sourceIdea.representative_idea,
          );
          if (ideaIndex !== -1) {
            cluster.ideas.splice(ideaIndex, 1);
            removed = true;
            break;
          }
        }

        if (removed) {
          $q.notify({
            type: 'positive',
            message: t('consultation.ai.mergeIdeas.success', {
              count: targetIdea.answer_ids.length - originalCount,
            }),
            position: 'top',
          });
        }
      }

      onSave();
    });
  }

  function moveIdeaToTheme(sourceIdea: any, targetTheme: Cluster, sourceThemeName: string) {
    const clusters = insights.value?.clusters;
    if (!clusters) return;

    const sourceCluster = clusters.find((c) => c.theme === sourceThemeName);
    if (!sourceCluster) return;

    if (sourceCluster.theme === targetTheme.theme) {
      return;
    }

    const ideaIndex = sourceCluster.ideas.findIndex(
      (idea) => idea.representative_idea === sourceIdea.representative_idea,
    );
    if (ideaIndex === -1) return;

    const [movedIdea] = sourceCluster.ideas.splice(ideaIndex, 1);
    targetTheme.ideas.push(movedIdea);

    $q.notify({
      type: 'positive',
      message: t('consultation.ai.moveIdea.success', { target: targetTheme.theme }),
      position: 'top',
    });

    onSave();
  }

  return {
    dragOverElement,
    editingItems,
    isDragDisabled,
    startEdit,
    finishEdit,
    onDragStart,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    moveIdeaToTheme,
    mergeIdeas,
    mergeThemes,
  };
}
